import React, { forwardRef, useState, useEffect } from 'react'
import cn from '../../lib/utils/cn'
import { useFocusWithin } from '@mantine/hooks'
import LockerLoader from '../common/LockerLoader'

interface TextareaProps {
	label?: string
	wrapperClassName?: string
	labelClassName?: string
	textareaClassName?: string
	errorClassName?: string
	limitTooltipClassName?: string
	error?: string
	placeholder?: string
	value?: string
	onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
	onEnterClick?: () => void
	rows?: number
	tooltip?: string
	maxLength?: number
	required?: boolean
	withLimitWarning?: boolean
	limitMessage?: string
	disabled?: boolean
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
	(props, ref) => {
		const {
			label,
			wrapperClassName,
			labelClassName = '',
			textareaClassName = '',
			errorClassName = '',
			limitTooltipClassName = '',
			placeholder = '',
			error,
			value,
			onChange,
			onEnterClick,
			rows = 3,
			maxLength,
			required,
			withLimitWarning = false,
			limitMessage,
			disabled,
		} = props

		const { ref: focusRef, focused } = useFocusWithin()
		const [showWarning, setShowWarning] = useState(false)

		useEffect(() => {
			if (withLimitWarning && maxLength) {
				const remainingChars = maxLength - Number(value?.length)
				setShowWarning(remainingChars <= 0)
			}
		}, [value, maxLength, withLimitWarning])

		const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === 'Enter' && focused && onEnterClick) {
				e.preventDefault()
				onEnterClick()
			}
		}

		return (
			<div
				className={cn(
					'relative flex flex-col w-full h-fit box-border',
					wrapperClassName
				)}
			>
				{label && (
					<label
						className={cn(
							'text-text-main dark:text-text-main-dark font-semibold text-base mb-2 flex items-center gap-2.5',
							labelClassName
						)}
					>
						<span>
							{label}
							{required && (
								<span className="text-accent dark:text-accent"> *</span>
							)}
						</span>
						{error && (
							<span
								className={cn('text-negative text-sm font-normal break-words w-full line-clamp-2 mb-1', 'text-negative text-base text-nowrap w-fit',
									errorClassName
								)}
							>
								{error}
							</span>
						)}
					</label>
				)}

				<textarea
					ref={focusRef}
					placeholder={placeholder}
					value={value}
					onChange={onChange}
					onKeyDown={handleKeyDown}
					rows={rows}
					maxLength={maxLength}
					className={cn(
						'h-11 w-auto relative bg-background-input dark:bg-background-input-dark dark:border focus:outline-stroke-dar dark:border-stroke-light-dark outline outline-stroke-dark dark:outline-stroke-light-dark rounded-lg outline-1 placeholder:text-stroke-dark dark:placeholder:text-stroke-dark font-roboto resize-none px-2 py-2.5 text-base text-text-main dark:text-text-main-dark md:text-sm',
						disabled && 'opacity-60 cursor-not-allowed bg-stroke-dark/10 dark:bg-stroke-dark/20',
						textareaClassName,
						error && 'outline-negative dark:outline-negative',
					)}
					disabled={disabled}
				/>

				{/* Limit Warning - Only show when limit reached AND focused */}
				{withLimitWarning && showWarning && focused && maxLength && (
					<div
						className={cn(
							'absolute -bottom-8 bg-text-secondary text-white inset-x-0 mx-auto py-1.5 px-3 rounded text-xs w-fit text-center whitespace-nowrap z-10',
							limitTooltipClassName
						)}
					>
						{limitMessage || `Maximum — ${maxLength} characters`}
					</div>
				)}
			</div>
		)
	}
)

Textarea.displayName = 'Textarea'

export default Textarea
